import React, { useEffect } from 'react';
import { useAtom } from 'jotai';
import { clockModeStateAtom } from '../../../../atoms/v3/clockModeAtoms';
import './themes/ClockModeThemes.css';

interface ClockModeThemeProviderProps {
  children: React.ReactNode;
}

/**
 * 时钟模式主题提供者
 * 实现 CSS Zen Garden 风格的主题系统
 * 相同的 HTML 结构，通过 CSS 类名实现完全不同的视觉效果
 */
export const ClockModeThemeProvider: React.FC<ClockModeThemeProviderProps> = ({ children }) => {
  const [state] = useAtom(clockModeStateAtom);

  useEffect(() => {
    // 移除所有主题类
    const themeClasses = [
      'clock-mode-theme-light'
    ];

    document.body.classList.remove(...themeClasses);

    // 添加当前主题类
    document.body.classList.add(`clock-mode-theme-${state.theme}`);

    // 添加过渡效果类
    document.body.classList.add('clock-mode-theme-transition');

    // 清理函数
    return () => {
      document.body.classList.remove(...themeClasses);
      document.body.classList.remove('clock-mode-theme-transition');
    };
  }, [state.theme]);

  return (
    <>
      {children}
    </>
  );
};

/**
 * 获取主题显示名称
 */
export const getThemeDisplayName = (theme: string): string => {
  const themeNames: Record<string, string> = {
    light: '明亮'
  };

  return themeNames[theme] || theme;
};

/**
 * 获取主题图标
 */
export const getThemeIcon = (theme: string): React.ReactNode => {
  switch (theme) {
    case 'light':
      return (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
    default:
      return (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
        </svg>
      );
  }
};

/**
 * 主题选择器组件
 */
interface ThemeSelectorProps {
  currentTheme: string;
  onThemeChange: (theme: string) => void;
  className?: string;
}

export const ThemeSelector: React.FC<ThemeSelectorProps> = ({
  currentTheme,
  onThemeChange,
  className = ''
}) => {
  const themes = ['light'];

  return (
    <div className={`grid grid-cols-3 gap-2 ${className}`}>
      {themes.map((theme) => (
        <button
          key={theme}
          onClick={() => onThemeChange(theme)}
          className={`
            p-3 rounded-lg border-2 transition-all duration-200
            flex flex-col items-center space-y-1
            ${currentTheme === theme 
              ? 'border-blue-500 bg-blue-50 text-blue-700' 
              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }
          `}
        >
          <div className="text-lg">
            {getThemeIcon(theme)}
          </div>
          <span className="text-xs font-medium">
            {getThemeDisplayName(theme)}
          </span>
        </button>
      ))}
    </div>
  );
};
